import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import {
  userPortalService,
  LearningSessionItem,
} from "services/userPortalService";
import FaceCheckinPanel from "./FaceCheckinPanel";

export default function SelfCheckin() {
  const { t } = useLanguage();
  const { addNotification } = useNotification();
  const [searchParams] = useSearchParams();
  // When opened from a specific session's detail view (e.g. the course
  // schedule page), the session is pre-selected via this query param.
  // Falls back to manual dropdown selection when absent (e.g. reached
  // directly from the sidebar nav entry).
  const preselectedSessionId = searchParams.get("sessionId") || "";

  const [sessions, setSessions] = useState<LearningSessionItem[]>([]);
  const [selectedSession, setSelectedSession] = useState(preselectedSessionId);
  const [loadingSessions, setLoadingSessions] = useState(true);

  const preselectedSessionLabel = useMemo(() => {
    const match = sessions.find((s) => s.session_id === preselectedSessionId);
    if (!match) return null;
    return match.start_time
      ? `${match.title} — ${new Date(match.start_time).toLocaleString()}`
      : match.title;
  }, [sessions, preselectedSessionId]);

  useEffect(() => {
    userPortalService
      .getMyLearning()
      .then((data) => {
        const upcoming = data.upcoming_sessions ?? [];
        // Ensure a pre-selected session (e.g. passed from a session detail
        // view via ?sessionId=) is present in the dropdown even if it's not
        // part of the "upcoming" list (e.g. it's happening right now/today).
        if (
          preselectedSessionId &&
          !upcoming.some((s) => s.session_id === preselectedSessionId)
        ) {
          const fromPast = (data.past_sessions ?? []).find(
            (s) => s.session_id === preselectedSessionId
          );
          if (fromPast) {
            setSessions([fromPast, ...upcoming]);
            return;
          }
        }
        setSessions(upcoming);
      })
      .catch(() => {
        addNotification(t("selfCheckin.loadSessionError"), "error");
      })
      .finally(() => setLoadingSessions(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto mt-4 max-w-lg px-4">
      <h2 className="mb-4 text-2xl font-bold text-ink">
        {t("selfCheckin.title")}
      </h2>

      {/* Session context: when opened with a session already known (e.g.
          "Mở camera điểm danh" from a session's detail popup, which passes
          ?sessionId=...), just show which session it's for — no picker.
          Otherwise (e.g. reached directly from sidebar nav), fall back to
          the dropdown so the student can still pick one manually. */}
      {preselectedSessionId ? (
        preselectedSessionLabel && (
          <div className="mb-4 rounded-lg bg-brand-50 px-3 py-2 text-sm font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-200">
            {preselectedSessionLabel}
          </div>
        )
      ) : (
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-ink-muted">
            {t("selfCheckin.selectSession")}
          </label>
          <select
            className="w-full rounded-lg border border-gray-300 bg-surface px-3 py-2 text-sm dark:border-gray-600 dark:text-white"
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
            disabled={loadingSessions}
          >
            <option value="">{t("selfCheckin.noSession")}</option>
            {sessions.map((s) => (
              <option key={s.session_id} value={s.session_id}>
                {s.title}
                {s.start_time
                  ? ` — ${new Date(s.start_time).toLocaleString()}`
                  : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      <FaceCheckinPanel sessionId={selectedSession || undefined} />
    </div>
  );
}
